import * as THREE from 'three';
import { BIMElement, DuctElement, PipeElement } from './types';

/** Parse a "100 mm" / "0.1 m" style size parameter into metres. */
function parseMillimetres(value: unknown): number | null {
    if (typeof value === 'number' && isFinite(value)) return value > 5 ? value / 1000 : value;
    if (typeof value !== 'string') return null;
    const match = value.match(/([\d.]+)\s*(mm|cm|m)?/i);
    if (!match) return null;
    const n = parseFloat(match[1]);
    if (!isFinite(n)) return null;
    const unit = (match[2] ?? 'mm').toLowerCase();
    if (unit === 'm') return n;
    if (unit === 'cm') return n / 100;
    return n / 1000;
}

export interface Connector {
    id: string; // Unique ID for the connector
    elementId: string;
    position: THREE.Vector3;
    direction: THREE.Vector3; // Outward normal
    systemType: string;
    shape: 'Round' | 'Rectangular';
    size: number | [number, number]; // Diameter or [Width, Height]
}

export class ConnectorManager {
    /**
     * Get all connectors for a given element.
     */
    static getConnectors(element: BIMElement): Connector[] {
        const connectors: Connector[] = [];
        const geom = (element as any).geometryData;
        if (!geom) return connectors;

        if (element.category === 'Ducts') {
            const duct = element as DuctElement;
            const path = duct.geometryData.path;
            if (!path || path.length < 2) return connectors;
            const start = new THREE.Vector3(...path[0]);
            const end = new THREE.Vector3(...path[path.length - 1]);
            const dir = end.clone().sub(start).normalize();

            // Start Connector
            connectors.push({
                id: `${element.id}-start`,
                elementId: element.id,
                position: start,
                direction: dir.clone().negate(),
                systemType: element.parameters['System Type'] || 'Undefined',
                shape: 'Rectangular',
                size: [duct.geometryData.width, duct.geometryData.height]
            });

            // End Connector
            connectors.push({
                id: `${element.id}-end`,
                elementId: element.id,
                position: end,
                direction: dir,
                systemType: element.parameters['System Type'] || 'Undefined',
                shape: 'Rectangular',
                size: [duct.geometryData.width, duct.geometryData.height]
            });
        } else if (element.category === 'Pipes') {
            const pipe = element as PipeElement;
            const path = pipe.geometryData.path;
            if (!path || path.length < 2) return connectors;
            const start = new THREE.Vector3(...path[0]);
            const end = new THREE.Vector3(...path[path.length - 1]);
            const dir = end.clone().sub(start).normalize();

            // Start Connector
            connectors.push({
                id: `${element.id}-start`,
                elementId: element.id,
                position: start,
                direction: dir.clone().negate(),
                systemType: element.parameters['System Type'] || 'Undefined',
                shape: 'Round',
                size: pipe.geometryData.diameter
            });

            // End Connector
            connectors.push({
                id: `${element.id}-end`,
                elementId: element.id,
                position: end,
                direction: dir,
                systemType: element.parameters['System Type'] || 'Undefined',
                shape: 'Round',
                size: pipe.geometryData.diameter
            });
        } else if (element.category === 'Duct Fittings' || element.category === 'Pipe Fittings') {
            // Fittings: synthesize one connector per port from the authored
            // fitting data (position, rotation, angle, size). Elbows expose
            // two perpendicular ports, tees three, crosses four; the rotation
            // parameter orients the whole port set.
            const rotDeg = Number(geom.rotation ?? 0) || 0;
            const rot = THREE.MathUtils.degToRad(rotDeg);
            const axis = (deg: number): THREE.Vector3 => {
                const a = rot + THREE.MathUtils.degToRad(deg);
                return new THREE.Vector3(Math.cos(a), Math.sin(a), 0);
            };

            const type = String(element.type ?? '');
            let portAngles: number[];
            if (/cross/i.test(type)) portAngles = [0, 90, 180, 270];
            else if (/tee/i.test(type)) portAngles = [0, 90, 180];
            else if (/wye/i.test(type)) portAngles = [0, 45, 180];
            else {
                // Elbow (default): branch port at the authored angle.
                const branchAngle = Number(geom.angle ?? element.parameters?.['Angle'] ?? 90) || 90;
                portAngles = [0, branchAngle];
            }

            const isDuct = element.category === 'Duct Fittings';
            let size: number | [number, number];
            if (isDuct) {
                const width = Number(geom.width) || parseMillimetres(element.parameters?.['Size']) || 0.3;
                const height = Number(geom.height) || width;
                size = [width, height];
            } else {
                size = Number(geom.width) || parseMillimetres(element.parameters?.['Size']) || 0.1;
            }

            const position = new THREE.Vector3(...(geom.position ?? [0, 0, 0]));
            portAngles.forEach((deg, i) => {
                connectors.push({
                    id: `${element.id}-port${i}`,
                    elementId: element.id,
                    position,
                    direction: axis(deg),
                    systemType: element.parameters?.['System Type'] || 'Undefined',
                    shape: isDuct ? 'Rectangular' : 'Round',
                    size
                });
            });
        }

        return connectors;
    }

    /**
     * Find the closest connector to a given point within a tolerance.
     */
    static findClosestConnector(
        point: THREE.Vector3,
        elements: BIMElement[],
        tolerance: number = 0.2, // 20cm
        excludeElementId?: string
    ): Connector | null {
        let closest: Connector | null = null;
        let minDist = Infinity;

        for (const el of elements) {
            if (el.id === excludeElementId) continue;
            if (el.category !== 'Ducts' && el.category !== 'Pipes' && el.category !== 'Duct Fittings' && el.category !== 'Pipe Fittings') continue;

            const connectors = this.getConnectors(el);
            for (const conn of connectors) {
                const dist = point.distanceTo(conn.position);
                if (dist < tolerance && dist < minDist) {
                    minDist = dist;
                    closest = conn;
                }
            }
        }

        return closest;
    }
}
